import { useContext, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Controller, FormProvider, useFieldArray, useForm } from "react-hook-form";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import CardHeader from "@mui/material/CardHeader";
import CircularProgress from "@mui/material/CircularProgress";
import Divider from "@mui/material/Divider";
import FormControl from "@mui/material/FormControl";
import FormHelperText from "@mui/material/FormHelperText";
import Grid from "@mui/material/Grid";
import IconButton from "@mui/material/IconButton";
import InputLabel from "@mui/material/InputLabel";
import MenuItem from "@mui/material/MenuItem";
import Select from "@mui/material/Select";
import Stack from "@mui/material/Stack";
import TextField from "@mui/material/TextField";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import { AuthContext } from "../../../context/AuthContext";
import { useAlert } from "../../../context/AlertContext";
import {
  useCreateSummaryTemplate,
  useSummaryTemplate,
  useUpdateSummaryTemplate,
} from "../../../hooks/useSummaryTemplates";
import type {
  CreateSummaryTemplateRequest,
  LengthGuidance,
  OutputFormat,
  UpdateSummaryTemplateRequest,
} from "../../../types/summaryTemplate/SummaryTemplate";

const SUMMARY_TEMPLATE_FORM_ID = "summary-template-form";
const MAX_SECTIONS = 10;

interface SectionFormValues {
  heading: string;
  instructions: string;
}

interface TemplateFormValues {
  name: string;
  scope: "customer" | "personal";
  outputFormat: OutputFormat;
  lengthGuidance: LengthGuidance;
  globalInstructions: string;
  sections: SectionFormValues[];
}

const defaultValues: TemplateFormValues = {
  name: "",
  scope: "personal",
  outputFormat: "BulletPoints",
  lengthGuidance: "Standard",
  globalInstructions: "",
  sections: [{ heading: "", instructions: "" }],
};

const SummaryTemplateEditor = () => {
  const { t } = useTranslation("summaryTemplate");
  const { customerId, templateId } = useParams<{
    customerId: string;
    templateId: string;
  }>();
  const navigate = useNavigate();
  const { user } = useContext(AuthContext);
  const showAlert = useAlert();

  const isEditMode = !!templateId;
  const canManage = user?.hasPermission("summary-templates:manage") ?? false;

  const { data: existingTemplate, isLoading: isLoadingTemplate } =
    useSummaryTemplate(customerId, templateId);

  const createMutation = useCreateSummaryTemplate(customerId);
  const updateMutation = useUpdateSummaryTemplate(customerId);

  const form = useForm<TemplateFormValues>({ defaultValues });
  const {
    control,
    handleSubmit,
    reset,
    register,
    formState: { errors, isSubmitting },
  } = form;

  const { fields, append, remove } = useFieldArray({
    control,
    name: "sections",
  });

  // Populate form when editing an existing template
  useEffect(() => {
    if (!isEditMode || !existingTemplate) return;
    reset({
      name: existingTemplate.name,
      scope: existingTemplate.userId ? "personal" : "customer",
      outputFormat: existingTemplate.outputFormat,
      lengthGuidance: existingTemplate.lengthGuidance,
      globalInstructions: existingTemplate.globalInstructions ?? "",
      sections: existingTemplate.sections
        .slice()
        .sort((a, b) => a.order - b.order)
        .map((s) => ({ heading: s.heading, instructions: s.instructions })),
    });
  }, [isEditMode, existingTemplate, reset]);

  const onSubmit = async (values: TemplateFormValues) => {
    const sections = values.sections.map((s, idx) => ({
      heading: s.heading,
      instructions: s.instructions,
      order: idx + 1,
    }));

    try {
      if (isEditMode && templateId) {
        const request: UpdateSummaryTemplateRequest = {
          name: values.name,
          sections,
          globalInstructions: values.globalInstructions || null,
          outputFormat: values.outputFormat,
          lengthGuidance: values.lengthGuidance,
        };
        await updateMutation.mutateAsync({ templateId, request });
      } else {
        const request: CreateSummaryTemplateRequest = {
          name: values.name,
          scope: values.scope,
          sections,
          globalInstructions: values.globalInstructions || null,
          outputFormat: values.outputFormat,
          lengthGuidance: values.lengthGuidance,
        };
        await createMutation.mutateAsync(request);
      }
      showAlert(t("saveSuccess"), "success");
      navigate(-1);
    } catch {
      showAlert(t("saveError", { ns: "errors" }), "error");
    }
  };

  const handleCancel = () => navigate(-1);

  if (isEditMode && isLoadingTemplate) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight={200}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <FormProvider {...form}>
      <div className="flex m-auto flex-col h-3/4">
        <Card className="flex flex-col">
          <CardHeader
            title={isEditMode ? t("edit") : t("newTemplate")}
          />
          <CardContent className="flex-1 overflow-y-auto">
            <form id={SUMMARY_TEMPLATE_FORM_ID} onSubmit={handleSubmit(onSubmit)}>
              {/* Name + Scope row */}
              <Grid container spacing={2} sx={{ mb: 3 }}>
                <Grid item xs={12} sm={isEditMode ? 12 : 8}>
                  <TextField
                    label={t("templateName")}
                    fullWidth
                    required
                    error={!!errors.name}
                    helperText={errors.name?.message}
                    {...register("name", { required: t("templateName") + " is required" })}
                  />
                </Grid>
                {!isEditMode && (
                  <Grid item xs={12} sm={4}>
                    <Controller
                      name="scope"
                      control={control}
                      render={({ field }) => (
                        <FormControl fullWidth>
                          <InputLabel id="scope-label">{t("scope")}</InputLabel>
                          <Select
                            {...field}
                            labelId="scope-label"
                            label={t("scope")}
                          >
                            <MenuItem value="personal">{t("scopePersonal")}</MenuItem>
                            {canManage && (
                              <MenuItem value="customer">{t("scopeCustomer")}</MenuItem>
                            )}
                          </Select>
                        </FormControl>
                      )}
                    />
                  </Grid>
                )}
              </Grid>

              {/* Output format + Length row */}
              <Grid container spacing={2} sx={{ mb: 3 }}>
                <Grid item xs={12} sm={6}>
                  <Controller
                    name="outputFormat"
                    control={control}
                    render={({ field }) => (
                      <FormControl fullWidth>
                        <InputLabel id="output-format-label">{t("outputFormat")}</InputLabel>
                        <Select
                          {...field}
                          labelId="output-format-label"
                          label={t("outputFormat")}
                        >
                          <MenuItem value="BulletPoints">{t("bulletPoints")}</MenuItem>
                          <MenuItem value="NumberedList">{t("numberedList")}</MenuItem>
                          <MenuItem value="Prose">{t("prose")}</MenuItem>
                        </Select>
                      </FormControl>
                    )}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Controller
                    name="lengthGuidance"
                    control={control}
                    render={({ field }) => (
                      <FormControl fullWidth>
                        <InputLabel id="length-label">{t("length")}</InputLabel>
                        <Select
                          {...field}
                          labelId="length-label"
                          label={t("length")}
                        >
                          <MenuItem value="Brief">{t("brief")}</MenuItem>
                          <MenuItem value="Standard">{t("standard")}</MenuItem>
                          <MenuItem value="Detailed">{t("detailed")}</MenuItem>
                        </Select>
                      </FormControl>
                    )}
                  />
                </Grid>
              </Grid>

              {/* Global instructions */}
              <Box sx={{ mb: 3 }}>
                <TextField
                  label={t("globalInstructions")}
                  placeholder={t("globalInstructionsPlaceholder")}
                  fullWidth
                  multiline
                  minRows={3}
                  helperText={t("globalInstructionsHint")}
                  {...register("globalInstructions")}
                />
              </Box>

              {/* Sections */}
              <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 1 }}>
                {t("sections")}
              </Typography>

              {fields.map((field, index) => (
                <Card
                  key={field.id}
                  variant="outlined"
                  sx={{ mb: 2, borderLeft: "4px solid #1976d2" }}
                >
                  <CardContent>
                    <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
                      <Typography variant="body2" color="text.secondary">
                        {t("sections")} {index + 1}
                      </Typography>
                      {fields.length > 1 && (
                        <Tooltip title={t("removeSection")}>
                          <IconButton
                            size="small"
                            color="error"
                            onClick={() => remove(index)}
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      )}
                    </Stack>

                    <TextField
                      label={t("sectionHeading")}
                      fullWidth
                      required
                      error={!!errors.sections?.[index]?.heading}
                      helperText={errors.sections?.[index]?.heading?.message}
                      sx={{ mb: 2 }}
                      {...register(`sections.${index}.heading`, {
                        required: t("sectionHeading") + " is required",
                      })}
                    />

                    <TextField
                      label={t("sectionInstructions")}
                      placeholder={t("sectionInstructionsPlaceholder")}
                      fullWidth
                      required
                      multiline
                      minRows={3}
                      error={!!errors.sections?.[index]?.instructions}
                      helperText={errors.sections?.[index]?.instructions?.message}
                      {...register(`sections.${index}.instructions`, {
                        required: t("sectionInstructions") + " is required",
                      })}
                    />
                  </CardContent>
                </Card>
              ))}

              {fields.length < MAX_SECTIONS && (
                <Button
                  variant="outlined"
                  startIcon={<AddIcon />}
                  onClick={() => append({ heading: "", instructions: "" })}
                  sx={{ mb: 2 }}
                >
                  {t("addSection")}
                </Button>
              )}

              {fields.length >= MAX_SECTIONS && (
                <FormHelperText sx={{ mb: 2 }}>
                  Maximum {MAX_SECTIONS} sections allowed.
                </FormHelperText>
              )}
            </form>
          </CardContent>

          <Box className="sticky bottom-0 bg-white h-fit z-50">
            <Divider />
            <Stack className="p-2" direction="row" spacing={2}>
              <Button
                form={SUMMARY_TEMPLATE_FORM_ID}
                type="submit"
                variant="contained"
                disabled={isSubmitting}
              >
                {t("saveTemplate")}
              </Button>
              <Button onClick={handleCancel} color="secondary" variant="contained">
                {t("cancel", { ns: "common" })}
              </Button>
            </Stack>
          </Box>
        </Card>
      </div>
    </FormProvider>
  );
};

export default SummaryTemplateEditor;
